import Alpine from 'alpinejs'
// @ts-expect-error - @alpinejs/intersect ships no types
import intersect from '@alpinejs/intersect'
// @ts-expect-error - @alpinejs/focus ships no types
import focus from '@alpinejs/focus'

type Heading = {
    id: string
    index: number
}

declare module 'alpinejs' {
    interface Stores {
        headingsNavigator: {
            _activeHeadings: Heading[]
            openHeading: string
            readonly activeHeadingId: string | undefined
            addHeading: (heading: Heading) => void
            removeHeading: (heading: Heading) => void
            toggleOpenHeading: (headingId: string) => void
        }
    }
}

Alpine.plugin(intersect)
Alpine.plugin(focus)

type AgendaItem = {
    startDate_timestamp?: number | null
    teachers?: { title?: string | null }[]
    location?: (string | null)[]
}

type SearchResultLike = {
    agendaItems?: AgendaItem[]
}

Alpine.magic('nextAgenda', () => (result: SearchResultLike) => {
    const items = (result?.agendaItems ?? [])
        .filter((i): i is AgendaItem & { startDate_timestamp: number } => typeof i.startDate_timestamp === 'number')
        .sort((a, b) => a.startDate_timestamp - b.startDate_timestamp)

    if (!items.length) {
        return null
    }

    const now = Math.floor(Date.now() / 1000)
    const item = items.find((i) => i.startDate_timestamp >= now) ?? items[0]
    const date = new Date(item.startDate_timestamp * 1000)

    return {
        iso: date.toISOString().slice(0, 10),
        formatted: date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }),
        teacher: item.teachers?.[0]?.title ?? null,
        location: item.location?.find((l): l is string => typeof l === 'string' && l.length > 0) ?? null,
    }
})

type NewsResultLike = {
    postDate_timestamp?: number | null
    author?: { fullName?: string | null } | null
}

Alpine.magic('newsMeta', () => (result: NewsResultLike) => {
    if (typeof result?.postDate_timestamp !== 'number' || result.postDate_timestamp <= 0) {
        return null
    }

    const date = new Date(result.postDate_timestamp * 1000)

    return {
        iso: date.toISOString().slice(0, 10),
        formatted: date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }),
        author: result.author?.fullName ?? null,
    }
})

Alpine.store('headingsNavigator', {
    _activeHeadings: [],
    openHeading: '',

    get activeHeadingId() {
        const lastHeading = this._activeHeadings.sort(
            (a: Heading, b: Heading) => a.index - b.index
        )[this._activeHeadings.length - 1]

        return lastHeading?.id
    },

    addHeading(heading: Heading) {
        const headingIndex = this._activeHeadings.findIndex(
            (activeHeading) => activeHeading.id === heading.id
        )

        if (headingIndex === -1) {
            this._activeHeadings.push(heading)
        }
    },
    removeHeading(heading: Heading) {
        const headingIndex = this._activeHeadings.findIndex(
            (activeHeading) => activeHeading.id === heading.id
        )

        if (headingIndex !== -1) {
            this._activeHeadings.splice(1, headingIndex)
        }
    },
    toggleOpenHeading(headingId: string) {
        this.openHeading = this.openHeading === headingId ? '' : headingId
    },
})

window.Alpine = Alpine
